const curveData = [
  { month: 'Jan', oos: 100, is: 100 },
  { month: 'Feb', oos: 103, is: 106 },
  { month: 'Mar', oos: 108, is: 114 },
  { month: 'Apr', oos: 106, is: 118 },
  { month: 'May', oos: 112, is: 126 },
  { month: 'Jun', oos: 110, is: 130 },
  { month: 'Jul', oos: 116, is: 138 },
  { month: 'Aug', oos: 121, is: 148 },
  { month: 'Sep', oos: 118, is: 153 },
  { month: 'Oct', oos: 124, is: 162 },
  { month: 'Nov', oos: 128, is: 170 },
  { month: 'Dec', oos: 131, is: 176 },
];

const periodBoundaries = [2, 5, 8]; // Vertical lines between walk-forward periods

function scaledY(value: number, min: number, max: number, height: number): number {
  return height - ((value - min) / (max - min)) * height;
}

export default function CompositeEquityCurveMock() {
  const width = 420;
  const height = 140;
  const padding = { left: 36, right: 12, top: 12, bottom: 24 };
  const chartW = width - padding.left - padding.right;
  const chartH = height - padding.top - padding.bottom;

  const allValues = curveData.flatMap((d) => [d.oos, d.is]);
  const min = Math.min(...allValues) - 5;
  const max = Math.max(...allValues) + 5;

  const step = chartW / (curveData.length - 1);

  const oosPath = curveData
    .map((d, i) => `${i === 0 ? 'M' : 'L'}${padding.left + i * step},${padding.top + scaledY(d.oos, min, max, chartH)}`)
    .join(' ');
  const isPath = curveData
    .map((d, i) => `${i === 0 ? 'M' : 'L'}${padding.left + i * step},${padding.top + scaledY(d.is, min, max, chartH)}`)
    .join(' ');

  // Y-axis ticks
  const yTicks = [100, 120, 140, 160, 180];

  return (
    <div className="platform-mock">
      <div className="platform-mock-header"><span className="dot" /> Composite Equity Curve</div>
      <div className="platform-mock-body" style={{ padding: '0.75rem' }}>
        <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height: 'auto' }}>
          {/* Y-axis labels */}
          {yTicks.map((v) => (
            <g key={v}>
              <line
                x1={padding.left}
                y1={padding.top + scaledY(v, min, max, chartH)}
                x2={padding.left + chartW}
                y2={padding.top + scaledY(v, min, max, chartH)}
                stroke="#334155"
                strokeWidth={0.5}
              />
              <text
                x={padding.left - 4}
                y={padding.top + scaledY(v, min, max, chartH) + 3}
                textAnchor="end"
                fill="#64748b"
                fontSize={8}
              >
                {v}
              </text>
            </g>
          ))}

          {/* Period boundaries */}
          {periodBoundaries.map((idx) => (
            <line
              key={idx}
              x1={padding.left + idx * step}
              y1={padding.top}
              x2={padding.left + idx * step}
              y2={padding.top + chartH}
              stroke="#475569"
              strokeWidth={0.8}
              strokeDasharray="3,3"
            />
          ))}

          {/* In-sample curve (dashed, lighter) */}
          <path d={isPath} fill="none" stroke="#60a5fa" strokeWidth={1.5} strokeDasharray="4,3" opacity={0.5} />

          {/* OOS composite curve (solid, prominent) */}
          <path d={oosPath} fill="none" stroke="#22c55e" strokeWidth={2} />

          {/* X-axis labels */}
          {curveData.map((d, i) =>
            i % 2 === 0 ? (
              <text
                key={i}
                x={padding.left + i * step}
                y={height - 4}
                textAnchor="middle"
                fill="#64748b"
                fontSize={7}
              >
                {d.month}
              </text>
            ) : null
          )}
        </svg>

        <div style={{ display: 'flex', gap: '1.25rem', justifyContent: 'center', marginTop: '0.5rem', fontSize: '0.625rem', color: '#94a3b8' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
            <span style={{ width: 16, height: 2, background: '#22c55e', display: 'inline-block', borderRadius: 1 }} />
            OOS Composite (+31.0%)
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
            <span style={{ width: 16, height: 2, background: '#60a5fa', opacity: 0.5, display: 'inline-block', borderRadius: 1, borderTop: '1px dashed #60a5fa' }} />
            In-Sample (+76.0%)
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
            <span style={{ width: 12, height: 0, borderTop: '1px dashed #475569', display: 'inline-block' }} />
            Period boundary
          </span>
        </div>
      </div>
      <div className="platform-mock-caption">The composite curve stitches out-of-sample results from each window into one continuous equity path.</div>
    </div>
  );
}
